import { useState } from "react";
import { Lightbulb, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { api, visitorId } from "@/lib/api";

const KINDS = [["idea", "Idea"], ["request", "Request"], ["problem", "Something's not working"]];

export const FeedbackDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "idea", message: "", name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const onOpen = (o) => {
    if (o) setF({ kind: "idea", message: "", name: user?.name || "", email: user?.email || "" });
    setOpen(o);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (f.message.trim().length < 3) return toast.error("Write a few words first");
    setBusy(true);
    try {
      await api.sendFeedback({ ...f, visitor: visitorId(), page: window.location.pathname });
      toast.success("Thank you! Your message was sent.");
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err?.response?.status === 422 ? "Please check the email address" : "Couldn't send — please try again");
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <button className="group inline-flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300 transition-colors" data-testid="feedback-open-button">
          <Lightbulb size={15} className="text-amber-300" /> Have an idea or a request? Tell us
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-[#0F1520] border-white/10" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Help us make The Bucketlist better</DialogTitle>
          <DialogDescription>Ideas, requests or something that isn't working — we read every message.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="feedback-kind">
            {KINDS.map(([k, l]) => (
              <button type="button" key={k} onClick={() => setF((x) => ({ ...x, kind: k }))} data-testid={`feedback-kind-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.kind === k ? "bg-emerald-400 border-emerald-400 text-[#04130C] font-semibold" : "border-white/10 text-slate-300 hover:border-white/30"}`}>{l}</button>
            ))}
          </div>
          <Textarea rows={5} maxLength={3000} placeholder="Tell us what you'd love to see or what went wrong…" value={f.message} onChange={set("message")} data-testid="feedback-message" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name (optional)" maxLength={100} value={f.name} onChange={set("name")} data-testid="feedback-name" />
            <Input placeholder="Email (optional)" type="email" maxLength={200} value={f.email} onChange={set("email")} data-testid="feedback-email" />
          </div>
          <p className="text-[11px] text-slate-500">Leave your email if you'd like a reply.</p>
          <Button type="submit" disabled={busy} className="w-full bg-emerald-400 text-[#04130C] hover:bg-emerald-300" data-testid="feedback-submit">{busy ? "Sending…" : "Send message"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
